/// Flight strip — a side panel listing every active aircraft. Click
/// any row to pan the radar to that aircraft and select it. Polls
/// the supplied `getAll` accessor every 250ms so the list stays fresh
/// without burning React renders at 60 fps.

import { useEffect, useState } from 'react'
import type { AircraftSummary } from '../state/store'
import { compactGoal } from '../types/LevelData'
import type { AircraftGoal } from '../types/LevelData'

const POLL_MS = 250

/// Trimmed-down summary the strip needs. We accept the existing
/// AircraftSummary plus a goal accessor (the summary doesn't carry
/// the raw goal — only the human-readable description).
export interface FlightStripRow extends AircraftSummary {
  goal: AircraftGoal
}

export function FlightStrip(props: {
  getAll: () => FlightStripRow[]
  selectedId: string | null
  onSelect: (id: string) => void
  onClose: () => void
}) {
  const [rows, setRows] = useState<FlightStripRow[]>(() => props.getAll())

  useEffect(() => {
    const id = window.setInterval(() => setRows(props.getAll()), POLL_MS)
    return () => window.clearInterval(id)
  }, [props.getAll])

  return (
    // Two layouts share this DOM:
    //   • Phone (<md): bottom sheet, full width, up to 60vh.
    //     Rows render in a two-column grid so the player can scan
    //     six callsigns without scrolling on a typical 375 px
    //     screen — phone-relevant info only (callsign + FL +
    //     status glyph). The full row layout shows under desktop.
    //   • Desktop (md+): right-side panel like before.
    <aside className="pointer-events-auto absolute z-20 overflow-hidden bg-slate-900/85 text-sm text-slate-100 shadow-xl ring-1 ring-white/10 backdrop-blur
      inset-x-0 bottom-0 max-h-[60vh] rounded-t-2xl
      md:inset-x-auto md:bottom-auto md:right-4 md:top-16 md:w-72 md:max-h-[calc(100vh-6rem)] md:rounded-lg">
      <header className="flex items-center justify-between border-b border-white/10 px-4 py-2">
        <div className="text-sm font-semibold tracking-wide">
          Flight strip
          <span className="ml-2 font-normal text-slate-400">
            ({rows.length})
          </span>
        </div>
        <button
          onClick={props.onClose}
          className="rounded px-2 py-0.5 text-xs text-slate-400 hover:bg-white/5 hover:text-white"
        >
          ✕
        </button>
      </header>
      {rows.length === 0 ? (
        <div className="px-4 py-3 text-xs text-slate-400">No aircraft on radar.</div>
      ) : (
        <ul
          className="overflow-auto
            grid grid-cols-2 gap-px bg-white/5 max-h-[calc(60vh-3rem)]
            md:flex md:flex-col md:gap-0 md:bg-transparent md:max-h-[calc(100vh-9rem)] md:divide-y md:divide-white/5"
        >
          {rows.map((row) => (
            <li
              key={row.id}
              onClick={() => props.onSelect(row.id)}
              className={
                'cursor-pointer bg-slate-900/90 px-3 py-2 text-xs hover:bg-white/5 md:bg-transparent ' +
                (row.id === props.selectedId ? '!bg-emerald-900/40' : '')
              }
            >
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className={
                    'font-semibold ' +
                    (row.emergency
                      ? 'text-rose-300'
                      : row.id === props.selectedId
                        ? 'text-emerald-200'
                        : 'text-slate-100')
                  }
                >
                  {row.flightNumber}
                  {row.emergency ? ' ⚠' : ''}
                </span>
                <span className="font-mono text-[11px] text-slate-400">
                  FL{Math.round(row.altitudeFt / 100).toString().padStart(3, '0')}
                  <span className="hidden md:inline"> {Math.round(row.speedKts)}kt</span>
                </span>
              </div>
              <div className="mt-0.5 flex items-baseline justify-between gap-2 text-[11px] text-slate-400">
                <span>
                  {statusGlyph(row.status)}
                  <span className="ml-1 hidden md:inline">{compactGoal(row.goal)}</span>
                </span>
                <span
                  className={
                    'font-mono ' +
                    (row.squawk === '7700' ? 'text-rose-300' : 'text-slate-500')
                  }
                  title="Squawk"
                >
                  {row.squawk}
                </span>
                <span className="font-mono">
                  {Math.round(row.headingDeg).toString().padStart(3, '0')}°
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </aside>
  )
}

function statusGlyph(status: AircraftSummary['status']): string {
  switch (status) {
    case 'landing':
      return 'LDG'
    case 'awaitingTakeoff':
      return 'HOLD'
    case 'takingOff':
      return 'TKO'
    case 'landed':
      return 'DOWN'
    case 'completed':
      return 'OFF'
    case 'enRoute':
    default:
      return 'ENR'
  }
}
